/**
 * Validation Configuration
 *
 * Centralized configuration for form validation across the application.
 * Following SoC, this file contains only validation rules and labels,
 * not UI or business logic.
 */

import type { FieldConfig } from '../lib/hooks/useFormValidation';

// ============================================================================
// Menu Page Validation
// ============================================================================

export const MENU_PAGE_FIELDS: FieldConfig[] = [
  { field: 'objectTypeValue', label: 'Object type', required: true },
  { field: 'sKeyValue', label: 'S-KEY', required: true },
  { field: 'pipingSpecValue', label: 'Piping spec', required: true },
  { field: 'systemGroupingValue', label: 'System grouping', required: true },
];

// ============================================================================
// Connector Binding Page Validation
// ============================================================================

export const CONNECTOR_BINDING_FIELDS: FieldConfig[] = [
  { field: 'endpoint1Value', label: 'END-POINT_1', required: true },
  { field: 'endpoint2Value', label: 'END-POINT_2', required: true },
  { field: 'endpoint3Value', label: 'END-POINT_3', required: false },
  { field: 'endpoint4Value', label: 'END-POINT_4', required: false },
  { field: 'materialValue', label: 'Material', required: true },
  { field: 'designStdValue', label: 'DesignStd', required: false },
  { field: 'pressureClassValue', label: 'PressureClass', required: false },
  { field: 'insulationSpecValue', label: 'INSULATION-SPEC', required: false },
  { field: 'insulationThicknessValue', label: 'INSULATIONTHICKNESS', required: false },
  { field: 'tracingSpecValue', label: 'TRACING-SPEC', required: false },
  { field: 'itemDescriptionValue', label: 'ITEM-DESCRIPTION', required: true },
];

// ============================================================================
// Bolt Connection Page Validation
// ============================================================================

// Core bolt fields - always required regardless of choice
export const BOLT_CONNECTION_FIELDS: FieldConfig[] = [
  { field: 'boltDiaValue', label: 'BOLT-DIA', required: true },
  { field: 'boltQuantityValue', label: 'BOLT-QUANTITY', required: true },
  { field: 'boltLengthValue', label: 'BOLT-LENGTH', required: true },
];

// Additional fields for gasket-based modeling (when "No" is selected)
// These are only required when boltModelingChoice.value === 'no'
export const BOLT_CONNECTION_CONDITIONAL_FIELDS: FieldConfig[] = [
  { field: 'boltSetActiveValue', label: 'BoltSetActive', required: true },
  { field: 'boltItemDescriptionValue', label: 'ITEM-DESCRIPTION', required: true },
  { field: 'boltMaterialValue', label: 'Material', required: true },
  { field: 'boltStandardValue', label: 'Standard', required: true },
];

// ============================================================================
// Additional Categories Page Validation
// ============================================================================

export const ADDITIONAL_CATEGORIES_FIELDS: FieldConfig[] = [
  { field: 'valveTagValue', label: 'VALVE TAG', required: true },
  { field: 'supportsTagValue', label: 'SUPPORTS TAG', required: true },
  { field: 'supportsSsrefValue', label: 'SUPPORTS SSREF', required: false },
  { field: 'instrumentTagValue', label: 'INSTRUMENT TAG', required: true },
];

// ============================================================================
// Process Properties Page Validation
// ============================================================================

export const PROCESS_CONDITIONS_FIELDS: FieldConfig[] = [
  { field: 'processConditionsValue', label: 'Process condition', required: true },
  { field: 'pipingSpecValuePC', label: 'PIPING-SPEC', required: true },
  { field: 'tracingSpecValuePC', label: 'TRACING-SPEC', required: false },
  { field: 'attribute12Value', label: 'Design pressure', required: true },
  { field: 'attribute13Value', label: 'Design temperature', required: true },
  { field: 'attribute14Value', label: 'Operating pressure', required: false },
  { field: 'attribute15Value', label: 'Operating temperature', required: false },
  { field: 'attribute16Value', label: 'Service description', required: false },
  { field: 'attribute17Value', label: 'PED group', required: false },
  { field: 'attribute18Value', label: 'PED category', required: false },
  { field: 'attribute19Value', label: 'Test pressure', required: false },
  { field: 'attribute20Value', label: 'Test type', required: false },
  { field: 'attribute21Value', label: 'Heat-tracing spec', required: false },
  { field: 'attribute22Value', label: 'Insulation thickness', required: false },
  { field: 'attribute23Value', label: 'Insulation type', required: false },
];

export const PROJECT_PROPERTIES_FIELDS: FieldConfig[] = [
  { field: 'projectPropertiesValue', label: 'Project property', required: true },
  { field: 'projectIdentifierValue', label: 'PROJECT-IDENTIFIER', required: true },
  { field: 'projectNameValue', label: 'Project name', required: true },
  { field: 'areaValue', label: 'AREA', required: false },
  { field: 'attribute1ClientName', label: 'Client name', required: false },
  { field: 'attribute2ProjectAddress', label: 'Project address', required: false },
  { field: 'attribute3ClientDepartment', label: 'Client department', required: false },
  { field: 'attribute4ClientProjectNumber', label: 'Client project number', required: false },
  { field: 'attribute5ClientDocumentNumber', label: 'Client document number', required: false },
  { field: 'attribute6ClientCode', label: 'Client code', required: false },
  { field: 'attribute7EngPartyOffice', label: 'Engineering party office', required: false },
  { field: 'attribute8EngPartyOfficeLocation', label: 'Engineering party office location', required: false },
  { field: 'attribute9EngPartyProjectNumber', label: 'Engineering party project number', required: false },
  { field: 'attribute10EngPartyAreaCode', label: 'Engineering party area code', required: false },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get label for a field
 */
export function getFieldLabel(fields: FieldConfig[], fieldName: string): string {
  const field = fields.find((f) => f.field === fieldName);
  return field?.label || fieldName;
}

/**
 * Check if a field is required
 */
export function isFieldRequired(fields: FieldConfig[], fieldName: string): boolean {
  const field = fields.find((f) => f.field === fieldName);
  return field?.required ?? false;
}

/**
 * Create conditional fields with a condition function
 */
export function withCondition(
  fields: FieldConfig[],
  condition: () => boolean
): FieldConfig[] {
  return fields.map((field) => ({
    ...field,
    condition,
  }));
}
